import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PenNib, ArrowRight, Copy, CheckCircle, Envelope, User } from '@phosphor-icons/react';
import ProgressBar from '@/components/ProgressBar';
import { toast } from 'sonner';
import { demoLeadsAPI } from '@/utils/api';

const DemoPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('intro'); // 'intro', 'capture', 'input', 'running', 'result'
  const [status, setStatus] = useState('pending');
  const [progressStep, setProgressStep] = useState(0);
  const [leadData, setLeadData] = useState({
    first_name: '',
    email: '',
  });
  const [leadLoading, setLeadLoading] = useState(false);
  const [formData, setFormData] = useState({
    niche: '',
    platform: 'Instagram',
    content_type: 'post',
    tone: 'professional',
    cta_type: 'engagement',
  });
  const [outputs, setOutputs] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLeadChange = (e) => {
    setLeadData({ ...leadData, [e.target.name]: e.target.value });
  };

  const handleStartDemo = () => {
    setStep('capture');
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadLoading(true);
    
    try {
      const response = await demoLeadsAPI.create(leadData);
      
      // Store lead email for signup pre-fill
      localStorage.setItem('paygbot_demo_lead', JSON.stringify({
        first_name: leadData.first_name,
        email: leadData.email
      }));
      
      toast.success(response.data.message || 'Demo access granted!');
      setStep('input');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLeadLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStep('running');
    setStatus('running');
    setProgressStep(0);

    const progressSteps = [
      'Starting Content Bot',
      'Analyzing niche',
      'Generating hooks',
      'Building post ideas',
      'Writing captions',
      'Finalizing hashtags',
      'Complete'
    ];

    // Animate through progress steps
    for (let i = 0; i < progressSteps.length; i++) {
      setProgressStep(i);
      await new Promise(resolve => setTimeout(resolve, 400));
    }

    // Generate mock data
    const mockOutputs = {
        hooks: [
          `Stop scrolling! This ${formData.niche} tip will change everything...`,
          `3 reasons why ${formData.niche} experts are switching to this method`,
          `I wish I knew this ${formData.niche} secret 5 years ago`,
          `The ${formData.niche} strategy that got me 10k followers in 30 days`,
          `Why everyone is talking about this ${formData.niche} trend`,
          `You're doing ${formData.niche} wrong (here's how to fix it)`,
          `The truth about ${formData.niche} that no one tells you`,
          `How to 10x your ${formData.niche} results in just 7 days`,
        ],
        post_ideas: [
          {
            title: `Share your best ${formData.niche} tool that saves time`,
            description: 'Create a carousel showing your top 3 tools with pros and cons'
          },
          {
            title: `Before/after transformation post`,
            description: `Show ${formData.niche} results with specific metrics and timeline`
          },
          {
            title: `5 ${formData.niche} mistakes beginners make`,
            description: 'Educational carousel with actionable solutions for each mistake'
          },
          {
            title: `Day-in-the-life showing your ${formData.niche} routine`,
            description: 'Behind-the-scenes content showing your process and authenticity'
          },
          {
            title: `Success story from your ${formData.niche} journey`,
            description: 'Share a specific win with details on how you achieved it'
          },
        ],
        captions: [
          {
            text: `Ready to level up your ${formData.niche} game? Here's what worked for me...`,
            useCase: 'Engagement-focused'
          },
          {
            text: `Let's talk about ${formData.niche}. I've tested everything so you don't have to. Here's what actually works 👇`,
            useCase: 'Educational'
          },
          {
            text: `This ${formData.niche} approach changed everything for me. Save this for later! 🔖`,
            useCase: 'Value-driven'
          },
        ],
        hashtags: [
          `${formData.niche.replace(' ', '')}`,
          `${formData.niche.replace(' ', '')}Tips`,
          `${formData.platform}${formData.niche.replace(' ', '')}`,
          'ContentCreator',
          'DigitalMarketing',
          'GrowthHacking',
          'SocialMediaTips',
          'OnlineBusiness',
          'Entrepreneurship',
          'ContentStrategy',
        ],
        best_practices: `This is a demo preview. Create an account to unlock all bots, save projects, and access unlimited runs.`,
      };

      setOutputs(mockOutputs);
      setStatus('completed');
      setTimeout(() => setStep('result'), 500);
      toast.success('Demo content generated!');
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">P</span>
              </div>
              <span className="text-xl font-bold text-slate-900">PayGBot</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-slate-900">
                Login
              </Link>
              <Link
                to="/signup"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Intro */}
        {step === 'intro' && (
          <div className="space-y-8" data-testid="demo-intro">
            {/* Demo Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <PenNib size={20} weight="duotone" className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Demo Mode</h3>
                  <p className="text-blue-800 mb-4">
                    This is a demo version of PayGBot. You can test the Content Bot to see how PayGBot works.
                  </p>
                  <div className="space-y-2 text-sm text-blue-700">
                    <div className="flex items-center space-x-2">
                      <CheckCircle size={16} weight="fill" />
                      <span>One free bot run</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle size={16} weight="fill" />
                      <span>See real outputs</span>
                    </div>
                    <div className="text-blue-600 font-medium mt-3">
                      Create an account to unlock all bots and save results.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Bot Preview */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center">
                  <PenNib size={32} weight="duotone" className="text-purple-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Content Bot Demo</h2>
                  <p className="text-slate-600">
                    Generate hooks, post ideas, captions, and hashtags for your niche
                  </p>
                </div>
              </div>

              <button
                onClick={handleStartDemo}
                data-testid="start-demo-button"
                className="w-full py-4 px-6 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
              >
                Start Demo
              </button>
            </div>
          </div>
        )}

        {/* Lead Capture Form */}
        {step === 'capture' && (
          <div className="space-y-8" data-testid="demo-capture">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PenNib size={32} weight="duotone" className="text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Almost there!</h2>
                <p className="text-slate-600">
                  Enter your details below to access your free demo
                </p>
              </div>

              <form onSubmit={handleLeadSubmit} className="space-y-4" data-testid="demo-lead-form">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    First Name *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User size={20} className="text-slate-400" />
                    </div>
                    <input
                      type="text"
                      name="first_name"
                      required
                      data-testid="demo-lead-name-input"
                      value={leadData.first_name}
                      onChange={handleLeadChange}
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                      placeholder="John"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Envelope size={20} className="text-slate-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      required
                      data-testid="demo-lead-email-input"
                      value={leadData.email}
                      onChange={handleLeadChange}
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={leadLoading}
                  data-testid="demo-lead-submit-button"
                  className="w-full py-4 px-6 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {leadLoading ? 'Please wait...' : 'Access Free Demo'}
                </button>
              </form>

              <p className="text-center text-xs text-slate-500 mt-4">
                We'll send you helpful tips. No spam, unsubscribe anytime.
              </p>
            </div>

            <button
              onClick={() => setStep('intro')}
              className="w-full text-center text-sm text-slate-500 hover:text-slate-700"
            >
              ← Back
            </button>
          </div>
        )}

        {/* Input Form */}
        {step === 'input' && (
          <form onSubmit={handleSubmit} data-testid="demo-content-bot-form" className="space-y-6">
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Content Bot Demo</h1>
              <p className="text-slate-600">Fill in the details below to generate content</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Your Niche *</label>
                <input
                  type="text"
                  name="niche"
                  required
                  data-testid="demo-niche-input"
                  value={formData.niche}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                  placeholder="e.g., fitness, digital marketing, cooking"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Platform *</label>
                <select
                  name="platform"
                  data-testid="demo-platform-select"
                  value={formData.platform}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                >
                  <option value="Instagram">Instagram</option>
                  <option value="TikTok">TikTok</option>
                  <option value="Facebook">Facebook</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Blog">Blog</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Content Type *</label>
                <select
                  name="content_type"
                  data-testid="demo-content-type-select"
                  value={formData.content_type}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                >
                  <option value="post">Post</option>
                  <option value="reel script">Reel Script</option>
                  <option value="video script">Video Script</option>
                  <option value="caption">Caption</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tone/Style *</label>
                <select
                  name="tone"
                  data-testid="demo-tone-select"
                  value={formData.tone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                >
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="humorous">Humorous</option>
                  <option value="inspirational">Inspirational</option>
                </select>
              </div>

              <button
                type="submit"
                data-testid="demo-submit-button"
                className="w-full py-4 px-6 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
              >
                Generate Content
              </button>
            </div>
          </form>
        )}

        {/* Running State */}
        {step === 'running' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6" data-testid="demo-running">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
              <h3 className="text-xl font-semibold text-slate-900">Running Content Bot...</h3>
              <p className="text-slate-600">Generating your content plan</p>
            </div>

            {/* Progress Steps */}
            <div className="max-w-2xl mx-auto space-y-3">
              {[
                { label: 'Starting Content Bot', icon: '🚀' },
                { label: 'Analyzing niche', icon: '🔍' },
                { label: 'Generating hooks', icon: '✨' },
                { label: 'Building post ideas', icon: '💡' },
                { label: 'Writing captions', icon: '✍️' },
                { label: 'Finalizing hashtags', icon: '🏷️' },
                { label: 'Complete', icon: '✅' },
              ].map((stepItem, index) => (
                <div
                  key={index}
                  className={`flex items-center space-x-4 p-4 rounded-lg transition-all duration-300 ${
                    index === progressStep
                      ? 'bg-purple-50 border-2 border-purple-500 scale-105'
                      : index < progressStep
                      ? 'bg-emerald-50 border border-emerald-200'
                      : 'bg-slate-50 border border-slate-200 opacity-50'
                  }`}
                  data-testid={`progress-step-${index}`}
                >
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      index === progressStep
                        ? 'bg-purple-500'
                        : index < progressStep
                        ? 'bg-emerald-500'
                        : 'bg-slate-300'
                    }`}
                  >
                    {index < progressStep ? (
                      <CheckCircle size={20} weight="fill" className="text-white" />
                    ) : (
                      <span className="text-lg">{stepItem.icon}</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <p
                      className={`font-medium ${
                        index === progressStep
                          ? 'text-purple-900'
                          : index < progressStep
                          ? 'text-emerald-900'
                          : 'text-slate-600'
                      }`}
                    >
                      {stepItem.label}
                    </p>
                  </div>
                  {index === progressStep && (
                    <div className="flex-shrink-0">
                      <div className="animate-pulse w-2 h-2 bg-purple-500 rounded-full"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {step === 'result' && outputs && (
          <div className="space-y-8" data-testid="demo-results">
            {/* Upgrade CTA */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-10 text-white shadow-xl">
              <div className="max-w-2xl mx-auto text-center space-y-6">
                <h3 className="text-3xl font-bold">Unlock the Full PayGBot System</h3>
                <p className="text-xl text-blue-100">
                  Get access to all 4 bots, unlimited runs, saved projects, and beginner-friendly business tools.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/signup?from=demo"
                    data-testid="demo-signup-cta"
                    onClick={() => localStorage.setItem('paygbot_from_demo', 'true')}
                    className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-white text-blue-600 hover:bg-blue-50 font-semibold rounded-lg transition-colors shadow-lg text-lg"
                  >
                    <span>Create My Account</span>
                    <ArrowRight size={20} weight="bold" />
                  </Link>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center justify-center px-8 py-4 bg-blue-800 hover:bg-blue-900 text-white font-medium rounded-lg transition-colors"
                  >
                    View Plans
                  </Link>
                </div>
              </div>
            </div>

            {/* Section 1: Hooks */}
            <div className="bg-white rounded-xl border-2 border-purple-200 shadow-sm overflow-hidden">
              <div className="bg-purple-50 border-b border-purple-200 px-8 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">1. Hooks</h3>
                  <p className="text-sm text-slate-600 mt-1">Attention-grabbing openers for your content</p>
                </div>
                <button
                  onClick={() => handleCopy(outputs.hooks.join('\n'))}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-purple-700 hover:text-purple-900 hover:bg-purple-100 rounded-lg transition-colors"
                >
                  <Copy size={18} weight="duotone" />
                  <span>Copy All</span>
                </button>
              </div>
              <div className="p-8">
                <ol className="space-y-3">
                  {outputs.hooks.map((hook, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </span>
                      <p className="text-slate-700 flex-1">{hook}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Section 2: Post Ideas */}
            <div className="bg-white rounded-xl border-2 border-emerald-200 shadow-sm overflow-hidden">
              <div className="bg-emerald-50 border-b border-emerald-200 px-8 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">2. Post Ideas</h3>
                  <p className="text-sm text-slate-600 mt-1">Structured content concepts ready to create</p>
                </div>
                <button
                  onClick={() => handleCopy(outputs.post_ideas.map((idea, i) => `${i + 1}. ${idea.title}\n${idea.description}`).join('\n\n'))}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100 rounded-lg transition-colors"
                >
                  <Copy size={18} weight="duotone" />
                  <span>Copy All</span>
                </button>
              </div>
              <div className="p-8 space-y-4">
                {outputs.post_ideas.map((idea, index) => (
                  <div key={index} className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900 mb-2">{idea.title}</h4>
                        <p className="text-sm text-slate-600">{idea.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3: Captions */}
            <div className="bg-white rounded-xl border-2 border-blue-200 shadow-sm overflow-hidden">
              <div className="bg-blue-50 border-b border-blue-200 px-8 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">3. Captions</h3>
                  <p className="text-sm text-slate-600 mt-1">Ready-to-use caption templates</p>
                </div>
                <button
                  onClick={() => handleCopy(outputs.captions.map(c => c.text).join('\n\n'))}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  <Copy size={18} weight="duotone" />
                  <span>Copy All</span>
                </button>
              </div>
              <div className="p-8 grid md:grid-cols-3 gap-4">
                {outputs.captions.map((caption, index) => (
                  <div key={index} className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-6 space-y-3">
                    <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                      {caption.useCase}
                    </div>
                    <p className="text-slate-700 text-sm leading-relaxed">{caption.text}</p>
                    <button
                      onClick={() => handleCopy(caption.text)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center space-x-1"
                    >
                      <Copy size={14} weight="duotone" />
                      <span>Copy</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 4: Hashtags */}
            <div className="bg-white rounded-xl border-2 border-amber-200 shadow-sm overflow-hidden">
              <div className="bg-amber-50 border-b border-amber-200 px-8 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">4. Hashtags</h3>
                  <p className="text-sm text-slate-600 mt-1">Optimized hashtag recommendations</p>
                </div>
                <button
                  onClick={() => handleCopy(outputs.hashtags.map(tag => `#${tag}`).join(' '))}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded-lg transition-colors"
                >
                  <Copy size={18} weight="duotone" />
                  <span>Copy All</span>
                </button>
              </div>
              <div className="p-8">
                <div className="flex flex-wrap gap-3">
                  {outputs.hashtags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors cursor-pointer"
                      onClick={() => handleCopy(`#${tag}`)}
                    >
                      <span>#{tag}</span>
                    </span>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-900">
                    <span className="font-semibold">Pro tip:</span> Use 5-8 hashtags per post. Mix popular and niche-specific tags for best reach.
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-8 text-center">
              <h3 className="text-xl font-semibold text-blue-900 mb-3">
                Ready to unlock all bots and features?
              </h3>
              <p className="text-blue-800 mb-6">
                Save your results, access all 4 bots, and get unlimited runs
              </p>
              <Link
                to="/signup?from=demo"
                data-testid="demo-bottom-cta"
                onClick={() => localStorage.setItem('paygbot_from_demo', 'true')}
                className="inline-flex items-center space-x-2 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                <span>Create My Account</span>
                <ArrowRight size={20} weight="bold" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoPage;